import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api, { directApi } from '../services/api';
import { getBestBoxImageUrl } from '../utils/imageUtils';
import MultiImageUploader from '../components/MultiImageUploader';
import { resizeImage } from '../utils/imageResizeUtils';
import LocationHierarchySelector from '../components/LocationHierarchySelector';
import demoService from '../services/demoService';

// Choose the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

const CreateBox = ({ isEditMode: isEditModeProp = false }) => {
  const navigate = useAppNavigation();
  const rawNavigate = useNavigate(); // For browser back navigation
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // Check if we're editing an existing box - either from props or query params
  const isEditMode = isEditModeProp || searchParams.get('edit') === 'true';
  // Get boxId from URL params if available, otherwise from query params
  const { boxId: routeBoxId } = useParams();
  const boxId = routeBoxId || searchParams.get('boxId');
  
  // Get prepopulated location from navigation state (e.g., from Locations page)
  const prepopulatedLocation = location.state?.prepopulatedLocation || '';
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [boxLocation, setBoxLocation] = useState(prepopulatedLocation);
  const [customImages, setCustomImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Fetch suggested name for new boxes
  useEffect(() => {
    const fetchSuggestedName = async () => {
      // Only fetch suggested name for new boxes (not edit mode)
      if (!isEditMode && !boxId) {
        try {
          const response = await apiInstance.get('/boxes/suggest-name');
          if (response.data?.suggested_name) {
            setName(response.data.suggested_name);
          }
        } catch (error) {
          console.error('Error fetching suggested box name:', error);
          // Not critical - user can still enter their own name
        }
      }
    };
    
    fetchSuggestedName();
  }, [isEditMode, boxId]);
  
  // Fetch box details if in edit mode
  useEffect(() => {
    const fetchBoxDetails = async () => {
      if (isEditMode && boxId) {
        setIsLoading(true);
        
        try {
          const response = await apiInstance.get(`/boxes/${boxId}`);
          const boxData = response.data;
          
          // Populate form fields
          setName(boxData.name || '');
          setDescription(boxData.description || '');
          setBoxLocation(boxData.location || '');
          
          // Check if box has a custom image
          if (boxData.custom_image_id) {
            // Use the new optimized box image function
            const imageUrl = getBestBoxImageUrl(boxData);
            
            console.log('Loading custom image through backend proxy:', imageUrl);
            
            // Test if the image URL is accessible before setting it
            const testImage = new Image();
            testImage.onload = () => {
              console.log('Custom image loaded successfully, setting in state');
              // Create a mock file object for the existing image
              const existingImage = {
                id: boxData.custom_image_id,
                url: imageUrl,
                name: 'existing-box-image.jpg'
              };
              
              // Set the image in state
              setCustomImages([existingImage]);
            };
            testImage.onerror = () => {
              console.log('Custom image failed to load, skipping initial image setup');
              // Don't set any initial image if it fails to load
              setCustomImages([]);
            };
            testImage.src = imageUrl;
          }
        } catch (error) {
          console.error('Error fetching box details:', error);
          setError('Could not load box details. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchBoxDetails();
  }, [isEditMode, boxId]);
  
  // Handle image selection from MultiImageUploader
  const handleImagesSelect = (selectedFiles) => {
    console.log('Selected files:', selectedFiles);
    
    if (!selectedFiles || selectedFiles.length === 0) {
      setCustomImages([]);
      return;
    }
    
    // Always use only the first image for box cover
    const firstFile = selectedFiles[0];
    
    // Store the original file in state - it will be resized right before form submission
    // This ensures we only upload ONE version of the image (the resized one)
    setCustomImages([firstFile]);
    
    // Log detailed information about the selected image
    console.log('Box image selected:', {
      name: firstFile.name,
      size: Math.round(firstFile.size / 1024) + ' KB',
      type: firstFile.type,
      lastModified: new Date(firstFile.lastModified).toISOString()
    });
  };
  
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter a box name');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Always use FormData for consistent image handling
      // This helps prevent duplicate uploads and ensures clean form submission
      
      let response;
      
      // Always use FormData for consistent image handling
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description || '');
      formData.append('location', boxLocation || '');
      
      // Check if we had an image initially but now it's been cleared
      // We can detect this by checking if we're in edit mode and customImages is empty
      if (isEditMode && customImages.length === 0) {
        // Signal to the backend that we're clearing the image
        formData.append('clear_image', 'true');
        console.log('Adding clear_image flag to form submission');
      }
      
      if (customImages && customImages.length > 0) {
        try {
          const originalImage = customImages[0];
          
          // Validate the image file exists and has the expected properties
          if (!originalImage || typeof originalImage !== 'object') {
            console.error('Invalid image file:', originalImage);
            throw new Error('Invalid image file');
          }
          
          console.log('Starting image processing for upload:', {
            name: originalImage.name,
            type: originalImage.type || 'undefined',
            size: originalImage.size ? `${Math.round(originalImage.size / 1024)} KB` : 'NaN KB'
          });
          
          // IMPORTANT: Always resize the image before upload to prevent duplicate uploads
          // The backend will only store this single resized version
          let imageToUse;
          try {
            // Resize to 500x500 max dimensions with quality 0.9 for box thumbnails
            const resizedImage = await resizeImage(originalImage, 500, 500, 0.9);
            
            if (resizedImage) {
              imageToUse = resizedImage;
              
              // Only log size info if both images have size property (i.e., they're File objects)
              if (originalImage.size && resizedImage.size) {
                console.log('Successfully resized image:', {
                  oldSize: `${Math.round(originalImage.size / 1024)} KB`,
                  newSize: `${Math.round(resizedImage.size / 1024)} KB`,
                  reduction: `${Math.round((1 - (resizedImage.size / originalImage.size)) * 100)}%`
                });
              } else {
                console.log('Successfully processed image (existing image object)');
              }
            } else {
              // If resize failed (e.g., in Firefox), use original but log this fact
              imageToUse = originalImage;
              console.warn('Image resize returned null, using original image instead');
            }
          } catch (resizeError) {
            console.error('Error during image resize:', resizeError);
            imageToUse = originalImage;
          }
          
          // Handle File objects vs existing image objects differently
          if (imageToUse instanceof File || imageToUse instanceof Blob) {
            // Create a new File object with original filename but possibly resized content
            const fileToUpload = new File([imageToUse], originalImage.name, {
              type: imageToUse.type,
              lastModified: new Date().getTime()
            });
            
            // Only append the processed image to FormData - NOT the original
            formData.append('custom_image', fileToUpload);
            
            // Log final upload details
            console.log('Uploading box image:', {
              filename: fileToUpload.name,
              type: fileToUpload.type,
              size: `${Math.round(fileToUpload.size / 1024)} KB`,
              wasResized: imageToUse !== originalImage
            });
          } else {
            // This is an existing image object - skip file upload
            console.log('Skipping image upload for existing image:', {
              name: imageToUse.name || 'unknown',
              id: imageToUse.id || 'unknown',
              url: imageToUse.url || 'unknown'
            });
            // Don't append anything to formData for existing images
          }
          
          // Immediately clear the customImages array to prevent any duplicate uploads
          // This is critical to ensure we don't upload both original and resized versions
          setCustomImages([]);
        } catch (error) {
          console.error('Error processing image for upload:', error);
          setError('Failed to process image. Please try again or use a different image.');
          setIsLoading(false);
          return; // Exit submission to prevent API call with invalid data
        }
      }
      
      // Make API request to create or update box
      if (isEditMode && boxId) {
        // Update existing box with FormData for image upload
        response = await api.put(`/boxes/${boxId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        console.log('Box updated successfully with custom image:', response.data);
        
        // Note: Tag extraction is for items, not boxes, so we skip it here
        
        // Success - navigate back to previous page
        setSuccess(true);
        
        // Store success state in localStorage for feedback
        localStorage.setItem('box_update_success', JSON.stringify({
          message: 'Box updated successfully!',
          timestamp: Date.now()
        }));
        
        // Navigate back to previous page immediately
        setTimeout(() => {
          rawNavigate(-1); // Go back to previous page
        }, 500); // Reduced timeout for better UX
      } else {
        // Create new box
        response = await apiInstance.post('/boxes', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Note: Tag extraction is for items, not boxes, so we skip it here
        
        setSuccess(true);
        
        // Mark demo feature as completed if in demo mode
        if (demoService.isDemoMode()) {
          demoService.markFeatureCompleted('create_box');
        }
        
        // For new boxes, go to the box detail page to show the created box
        // Use replace to remove CreateBox from history so back goes to Locations
        setTimeout(() => {
          // Pass origin info to BoxDetail so it knows where to go back to
          const origin = sessionStorage.getItem('createbox_origin');
          rawNavigate(`/app/box/${response.data.id}`, { 
            replace: true,
            state: { fromCreateBox: true, origin: origin ? JSON.parse(origin) : null }
          });
        }, 500); // Reduced timeout for better UX
      }
      // No 'else' branch needed - we always use FormData now
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} box:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} box. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={() => isEditMode ? rawNavigate(-1) : navigate.to.ADD()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {isEditMode ? 'Edit Box Details' : 'Create New Box'}
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Box {isEditMode ? 'updated' : 'created'} successfully!
          </Alert>
        )}
        
        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Box Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              required
              autoFocus
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Description (Optional)"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
            />
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <LocationHierarchySelector
                value={boxLocation}
                onChange={setBoxLocation}
                label="Location (Optional)"
                placeholder="Select where this box is stored..."
                disabled={isLoading}
                showSuggestions={true}
                boxName={name}
              />
            </Box>
            
            {/* Custom Box Image Section */}
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Custom Box Image (Optional)
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <MultiImageUploader
                  onFilesSelect={handleImagesSelect}
                  initialFiles={customImages}
                  loading={isLoading}
                  allowCamera={true}
                  maxFiles={1} // Only allow one image for box cover
                  previewHeight={200}
                  hideTitle={true}
                  hideMultiSelect={true}
                  hideInstructions={true}
                  buttonText="Select File"
                  key="box-image-uploader" // Add a key to ensure proper rendering
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Adding a custom image will help you identify this box at a glance. 
                  If no image is selected, the box will use an image from one of its items as a thumbnail.
                  You can either upload an image file or take a photo with your camera.
                </Typography>
              </Grid>
            </Grid>
            
            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => isEditMode ? rawNavigate(-1) : navigate.to.ADD()}
                disabled={isLoading}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || !name.trim()}
              >
                {isLoading ? <CircularProgress size={24} /> : (isEditMode ? 'Save Changes' : 'Create Box')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateBox;
